/**
 * AIWorkspace Component
 * Main workspace component for AI actions
 * 
 * FEATURES:
 * - Translate, Generate, Custom Prompt actions
 * - Horizontal scrollable cards with navigation arrows
 * - History section with localStorage persistence
 * - Collapsible history view
 * - ✅ FIX: Scroll vertical com header sticky
 * - ✅ FIX: Cards com altura dinâmica
 */
import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
  AlertCircle, ChevronUp, ChevronDown, ChevronLeft, ChevronRight,
  History, BookmarkPlus, Loader2, X, Tags, Calendar, MessageCircle, Shuffle,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { getWorkspaceColors } from './styles';
import { MAX_REGENERATIONS } from './constants';
import ResultCard from './ResultCard';
import { TranslateAction, GenerateResponseAction, CustomPromptAction } from './actions';
import ActionChip from './ActionChip';
import OriginNumberChip, { originLabel } from './OriginNumberChip';
import useContainerWidth from './hooks/useContainerWidth';
import { WORKSPACE_ACTION_KEYS, rouletteKey } from './hooks/useWorkspaceConfig';

// ============================================
// ScrollableCardRow Component (inline)
// ✅ FIX: align-items: flex-start para cards com altura dinâmica
// ============================================
const ScrollableCardRow = ({ children, workspaceColors }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 5);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 5);
  }, []);

  useEffect(() => {
    checkScroll();
    const scrollEl = scrollRef.current;
    if (scrollEl) {
      scrollEl.addEventListener('scroll', checkScroll);
      const resizeObserver = new ResizeObserver(checkScroll);
      resizeObserver.observe(scrollEl);
      return () => {
        scrollEl.removeEventListener('scroll', checkScroll);
        resizeObserver.disconnect();
      };
    }
  }, [children, checkScroll]);

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    const scrollAmount = 340;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  const hasChildren = React.Children.count(children) > 0;
  if (!hasChildren) return null;

  const styles = {
    wrapper: {
      position: 'relative',
      display: 'flex',
      alignItems: 'flex-start', // ✅ FIX: Era 'center', agora alinha no topo
    },
    scrollContainer: {
      display: 'flex',
      gap: 12,
      padding: '8px 40px',
      overflowX: 'auto',
      overflowY: 'hidden',
      flex: 1,
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
      alignItems: 'flex-start', // ✅ FIX: Cards alinhados no topo
    },
    arrowBtn: {
      position: 'absolute',
      top: 100, // ✅ FIX: Posição fixa em vez de 50%
      transform: 'translateY(-50%)',
      width: 32,
      height: 32,
      borderRadius: '50%',
      border: 'none',
      backgroundColor: workspaceColors.accentPrimary,
      color: workspaceColors.bgPrimary,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      zIndex: 10,
      transition: 'all 0.2s ease',
      opacity: 0.85,
    },
    arrowHidden: {
      opacity: 0,
      pointerEvents: 'none',
    },
  };

  return (
    <div style={styles.wrapper}>
      {/* Left Arrow */}
      <button
        style={{
          ...styles.arrowBtn,
          left: 4,
          ...(canScrollLeft ? {} : styles.arrowHidden),
        }}
        onClick={() => scroll('left')}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = '1';
          e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = '0.85';
          e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
        }}
      >
        <ChevronLeft size={18} />
      </button>

      {/* Scrollable Content */}
      <div ref={scrollRef} style={styles.scrollContainer} className="hide-scrollbar-workspace">
        {children}
      </div>

      {/* Right Arrow */}
      <button
        style={{
          ...styles.arrowBtn,
          right: 4,
          ...(canScrollRight ? {} : styles.arrowHidden),
        }}
        onClick={() => scroll('right')}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = '1';
          e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = '0.85';
          e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
        }}
      >
        <ChevronRight size={18} />
      </button>
    </div>
  );
};

// ============================================
// Main AIWorkspace Component
// ============================================
const AIWorkspace = ({
  // Workspace visibility
  isOpen,
  onToggleWorkspace,
  selectedCount,
  inputText = '',
  
  // Dropdown control
  activeDropdown,
  onToggleDropdown,
  onCloseDropdown,
  
  // Translate state
  translateLanguages,
  onToggleTranslateLanguage,
  translateResults,
  translateProcessing,
  translateError,
  hasTranslateResults,
  
  // Generate state (no longer needs language selection)
  generateResults,
  generateProcessing,
  generateError,
  hasGenerateResults,
  
  // Custom Prompt state
  customResults = [],
  customProcessing = false,
  customError = null,
  hasCustomResults = false,
  
  // History state
  historyItems = [],
  isHistoryOpen = false,
  onToggleHistory,
  onRemoveFromHistory,
  onClearHistory,
  hasHistory = false,

  // Config das tags do workspace — mapa { translate, generate, custom,
  // saved_messages, label, followup, feedback } indicando se cada acao
  // aparece como tag. Quando ausente, default e "todas ativas".
  workspaceConfig = null,

  // Callbacks para abrir os drawers correspondentes as tags configuraveis.
  // Quando a tag esta no workspace, clicar nela chama esses handlers — sao
  // os mesmos que o menu de 3 pontinhos do chat ja usa hoje.
  onOpenSavedMessages,
  onOpenLabels,
  onOpenFollowup,
  onOpenFeedback,
  onOpenWorkspaceConfig,

  // Callbacks
  onClear,
  onExecuteTranslate,
  onExecuteGenerate,
  onExecuteCustom,
  onUseResponse,
  onRegenerateTranslate,
  onRegenerateGenerate,
  onRegenerateCustom,
  onRemoveTranslateResult,
  onRemoveGenerateResult,
  onRemoveCustomResult,
  
  // Regeneration tracking
  getTranslateRemainingRegenerations,
  getGenerateRemainingRegenerations,
  getCustomRemainingRegenerations,
  regeneratingId = null,

  // Context preview for "Chamar o Agente" modal
  selectedMessagesPreview = [],

  // Roletas de transferencia (grupos configurados pelo admin na tela de
  // Etiquetas). Cada grupo disponivel ao operador vira um chip nomeado,
  // fixavel igual as demais acoes. transferRoulettes =
  // [{ id, name, allow_members, member_count }]. onExecuteRoulette(groupId)
  // distribui o contato atual por aquele grupo (round-robin).
  // rouletteProcessingId = id do grupo em distribuicao (spinner no chip).
  transferRoulettes = [],
  onExecuteRoulette,
  rouletteProcessingId = null,

  // Funcionalidade B — número de ORIGEM do contato (por onde a mensagem sai).
  // originOptions: opções permitidas; activeOrigin: a opção ativa (override por
  // contato ou padrão da thread ativa); onSelectOrigin(option|null) persiste a
  // escolha; allowSwitchOrigin: se o operador pode trocar.
  originOptions = [],
  activeOrigin = null,
  onSelectOrigin,
  allowSwitchOrigin = true,
}) => {
  const { colors, theme } = useTheme();
  const workspaceColors = getWorkspaceColors(colors, theme === 'dark');

  // Container-based responsive breakpoints (workspace lives inside the chat
  // panel, so viewport media queries don't reflect its real width).
  const [responsiveRef, containerWidth] = useContainerWidth();
  const isCompact = containerWidth > 0 && containerWidth < 720;
  const isVeryCompact = containerWidth > 0 && containerWidth < 520;

  const hasAnyResults = hasTranslateResults || hasGenerateResults || hasCustomResults;
  const isTranslateDisabled = !inputText?.trim();
  const isGenerateDisabled = selectedCount === 0;

  // Helper para checar se uma tag opcional esta ligada na config. Quando
  // workspaceConfig nao foi passado (usos legados do componente), tudo
  // aparece — preserva o comportamento anterior sem quebrar telas que ainda
  // nao foram migradas para o novo modelo.
  const isActionEnabled = (key) => {
    if (!workspaceConfig) {
      // Defaults: as 3 acoes IA originais + Mensagens Prontas sempre ligadas;
      // etiqueta/followup/comentario so quando explicitamente habilitadas.
      if (key === WORKSPACE_ACTION_KEYS.TRANSLATE) return true;
      if (key === WORKSPACE_ACTION_KEYS.GENERATE) return true;
      if (key === WORKSPACE_ACTION_KEYS.CUSTOM) return true;
      return false;
    }
    return !!workspaceConfig[key];
  };

  const showTranslate = isActionEnabled(WORKSPACE_ACTION_KEYS.TRANSLATE);
  const showGenerate = isActionEnabled(WORKSPACE_ACTION_KEYS.GENERATE);
  const showCustom = isActionEnabled(WORKSPACE_ACTION_KEYS.CUSTOM);
  const showSaved = isActionEnabled(WORKSPACE_ACTION_KEYS.SAVED);
  const showLabel = isActionEnabled(WORKSPACE_ACTION_KEYS.LABEL);
  const showFollowup = isActionEnabled(WORKSPACE_ACTION_KEYS.FOLLOWUP);
  const showFeedback = isActionEnabled(WORKSPACE_ACTION_KEYS.FEEDBACK);

  // Calculate dynamic max height
  const getMaxHeight = () => {
    if (!isOpen) return '0px';

    // Historico aberto ou ha resultados: usa altura relativa a viewport.
    if (isHistoryOpen && hasHistory) return '70vh';
    if (hasAnyResults) return '70vh';

    // Apenas botoes de acao — altura folgada pra acomodar wrap em telas
    // estreitas (3-4 linhas de tags em mobile).
    return '160px';
  };

  const workspaceStyles = {
    toggleBar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '8px 16px',
      backgroundColor: workspaceColors.bgTertiary,
      borderTop: `1px solid ${workspaceColors.borderColor}`,
      cursor: 'pointer',
      transition: 'background-color 0.15s ease',
      gap: 8,
    },
    toggleBarHandle: {
      width: 40,
      height: 4,
      borderRadius: 2,
      backgroundColor: workspaceColors.textTertiary,
      opacity: 0.5,
      transition: 'all 0.15s ease',
    },
    toggleBarText: {
      fontSize: 11,
      fontWeight: 600,
      color: workspaceColors.textTertiary,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      transition: 'color 0.15s ease',
    },
    toggleBarIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: workspaceColors.textTertiary,
      transition: 'color 0.15s ease',
    },
    // Container principal
    container: {
      backgroundColor: workspaceColors.workspaceBg || workspaceColors.bgTertiary,
      overflow: 'hidden',
      transition: 'max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      maxHeight: getMaxHeight(),
      display: 'flex',
      flexDirection: 'column',
    },
    // Area de acoes — sempre flex-wrap pra quebrar linha em telas estreitas
    // ou quando o usuario ativa muitas tags na config do workspace.
    actionsRow: {
      display: 'flex',
      alignItems: 'center',
      gap: isCompact ? 8 : 10,
      rowGap: 8,
      flexWrap: 'wrap',
      padding: isCompact ? '10px 12px' : '12px 16px',
      borderBottom: (hasAnyResults || (isHistoryOpen && hasHistory))
        ? `1px solid ${workspaceColors.borderColor}`
        : 'none',
      flexShrink: 0,
    },
    // ✅ Área de conteúdo com scroll vertical
    contentArea: {
      flex: 1,
      overflowY: 'auto',
      overflowX: 'hidden',
      scrollbarWidth: 'thin',
      scrollbarColor: `${workspaceColors.textTertiary}40 transparent`,
    },
    divider: {
      width: 1,
      height: 28,
      backgroundColor: workspaceColors.borderColor,
    },
    panelToggleGroup: {
      marginLeft: 'auto',
      display: 'flex',
      alignItems: 'center',
      gap: 6,
    },
    historyBtn: {
      padding: '6px 12px',
      borderRadius: 16,
      border: 'none',
      backgroundColor: isHistoryOpen
        ? `${workspaceColors.accentPrimary}20`
        : 'transparent',
      color: isHistoryOpen
        ? workspaceColors.accentPrimary
        : workspaceColors.textSecondary,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      fontSize: 12,
      fontWeight: 500,
      transition: 'all 0.15s ease',
    },
    historyBadge: {
      minWidth: 18,
      height: 18,
      padding: '0 5px',
      borderRadius: 9,
      backgroundColor: workspaceColors.accentPrimary,
      color: 'white',
      fontSize: 10,
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    selectedBadge: {
      // Mesmas metricas dos botoes historyBtn/savedBtn para o chip ficar do
      // mesmo tamanho na mesma linha (antes ele aparecia maior por causa do
      // fontWeight e do X em 16px desbalanceando o baseline).
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '6px 12px',
      backgroundColor: `${workspaceColors.accentPrimary}20`,
      color: workspaceColors.accentPrimary,
      borderRadius: 16,
      fontSize: 12,
      fontWeight: 500,
      lineHeight: 1,
    },
    clearBtn: {
      padding: 0,
      width: 16,
      height: 16,
      border: 'none',
      background: 'transparent',
      color: workspaceColors.accentPrimary,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 8,
      lineHeight: 1,
      flexShrink: 0,
    },
    errorContainer: {
      padding: '10px 16px',
      backgroundColor: `${workspaceColors.danger}15`,
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      color: workspaceColors.danger,
      fontSize: 13,
      flexShrink: 0,
    },
    // ✅ Header sticky DENTRO da contentArea
    historySectionHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '8px 16px',
      backgroundColor: workspaceColors.workspaceBg || workspaceColors.bgTertiary,
      borderBottom: `1px solid ${workspaceColors.borderColor}`,
      position: 'sticky',
      top: 0,
      zIndex: 10,
    },
    historySectionTitle: {
      fontSize: 11,
      fontWeight: 600,
      color: workspaceColors.textSecondary,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      display: 'flex',
      alignItems: 'center',
      gap: 6,
    },
    clearHistoryBtn: {
      padding: '4px 8px',
      border: 'none',
      background: 'transparent',
      color: workspaceColors.danger,
      cursor: 'pointer',
      fontSize: 11,
      fontWeight: 500,
      borderRadius: 4,
      transition: 'background-color 0.15s ease',
    },
    // ✅ Histórico cards container
    historyCardsContainer: {
      borderBottom: hasAnyResults ? `1px solid ${workspaceColors.borderColor}` : 'none',
      paddingBottom: 8,
    },
    // Current results section
    currentSectionHeader: {
      padding: '12px 16px 4px',
      fontSize: 11,
      fontWeight: 600,
      color: workspaceColors.textSecondary,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      backgroundColor: workspaceColors.workspaceBg || workspaceColors.bgTertiary,
      position: 'sticky',
      top: isHistoryOpen && hasHistory ? 37 : 0,
      zIndex: 9,
    },
  };

  return (
    <>
      {/* Toggle Bar */}
      <div 
        style={workspaceStyles.toggleBar}
        onClick={onToggleWorkspace}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = workspaceColors.bgSecondary;
          const handle = e.currentTarget.querySelector('.toggle-handle');
          const text = e.currentTarget.querySelector('.toggle-text');
          const icon = e.currentTarget.querySelector('.toggle-icon');
          if (handle) handle.style.backgroundColor = workspaceColors.accentPrimary;
          if (text) text.style.color = workspaceColors.accentPrimary;
          if (icon) icon.style.color = workspaceColors.accentPrimary;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = workspaceColors.bgTertiary;
          const handle = e.currentTarget.querySelector('.toggle-handle');
          const text = e.currentTarget.querySelector('.toggle-text');
          const icon = e.currentTarget.querySelector('.toggle-icon');
          if (handle) {
            handle.style.backgroundColor = workspaceColors.textTertiary;
            handle.style.opacity = '0.5';
          }
          if (text) text.style.color = workspaceColors.textTertiary;
          if (icon) icon.style.color = workspaceColors.textTertiary;
        }}
      >
        <div className="toggle-handle" style={workspaceStyles.toggleBarHandle} />
        <span className="toggle-text" style={workspaceStyles.toggleBarText}>
          {isOpen ? 'Fechar' : 'Abrir'} AI Workspace
        </span>
        {/* Funcionalidade B — label discreto do número de ORIGEM (hover = detalhe). */}
        {activeOrigin && (
          <span
            style={{
              fontSize: 11,
              color: workspaceColors.textTertiary,
              marginLeft: 'auto',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: 200,
            }}
            title={`Enviar por: ${activeOrigin.member_name || originLabel(activeOrigin)}${activeOrigin.is_default ? ' (número da conversa)' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            Enviar por: {originLabel(activeOrigin)}
          </span>
        )}
        <div className="toggle-icon" style={workspaceStyles.toggleBarIcon}>
          {isOpen ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
        </div>
      </div>

      {/* Workspace Content */}
      <div ref={responsiveRef} style={workspaceStyles.container}>
        {/* Errors - Sempre no topo */}
        {translateError && (
          <div style={workspaceStyles.errorContainer}>
            <AlertCircle size={16} />
            <span>Tradução: {translateError}</span>
          </div>
        )}
        {generateError && (
          <div style={workspaceStyles.errorContainer}>
            <AlertCircle size={16} />
            <span>Geração: {generateError}</span>
          </div>
        )}
        {customError && (
          <div style={workspaceStyles.errorContainer}>
            <AlertCircle size={16} />
            <span>Chamar o Agente: {customError}</span>
          </div>
        )}

        {/* Actions Row — wrap em qualquer largura */}
        <div style={workspaceStyles.actionsRow}>
          {/* Tags principais (IA) — controladas pela config */}
          {showTranslate && (
            <TranslateAction
              isDropdownOpen={activeDropdown === 'translate'}
              selectedLanguages={translateLanguages}
              onToggleDropdown={() => onToggleDropdown('translate')}
              onToggleLanguage={onToggleTranslateLanguage}
              onCloseDropdown={onCloseDropdown}
              onExecute={onExecuteTranslate}
              isProcessing={translateProcessing}
              isDisabled={isTranslateDisabled}
              disabledReason="Digite algo para traduzir"
            />
          )}

          {showGenerate && (
            <GenerateResponseAction
              onExecute={onExecuteGenerate}
              isProcessing={generateProcessing}
              isDisabled={isGenerateDisabled}
              disabledReason="Selecione mensagens"
            />
          )}

          {showCustom && (
            <CustomPromptAction
              onExecute={onExecuteCustom}
              isProcessing={customProcessing}
              selectedMessagesPreview={selectedMessagesPreview}
              isCompact={isVeryCompact}
            />
          )}

          {/* Tags secundarias — abrem os drawers compartilhados com o
              menu de 3 pontinhos do chat. Sao opcionais (config do
              workspace decide se aparecem). */}
          {showSaved && onOpenSavedMessages && (
            <ActionChip
              icon={BookmarkPlus}
              label="Mensagens Prontas"
              isActive={false}
              hasDropdown={false}
              onClick={onOpenSavedMessages}
            />
          )}

          {showLabel && onOpenLabels && (
            <ActionChip
              icon={Tags}
              label="Etapa"
              isActive={false}
              hasDropdown={false}
              onClick={onOpenLabels}
            />
          )}

          {showFollowup && onOpenFollowup && (
            <ActionChip
              icon={Calendar}
              label="Followup"
              isActive={false}
              hasDropdown={false}
              onClick={onOpenFollowup}
            />
          )}

          {showFeedback && onOpenFeedback && (
            <ActionChip
              icon={MessageCircle}
              label="Comentário"
              isActive={false}
              hasDropdown={false}
              onClick={onOpenFeedback}
            />
          )}

          {/* Roletas de transferencia — 1 chip por grupo do admin disponivel
              ao operador e fixado (config por operador, via roulette:<id>).
              Clicar distribui o contato atual por aquele grupo. */}
          {onExecuteRoulette && transferRoulettes.map((group) => (
            isActionEnabled(rouletteKey(group.id)) ? (
              <ActionChip
                key={`roulette-${group.id}`}
                icon={Shuffle}
                label={group.name}
                isActive={false}
                isProcessing={rouletteProcessingId === group.id}
                processingLabel="Distribuindo..."
                hasDropdown={false}
                onClick={() => onExecuteRoulette(group.id)}
              />
            ) : null
          ))}

          {/* Funcionalidade B — número de ORIGEM (por onde a mensagem sai). */}
          {onSelectOrigin && (
            <OriginNumberChip
              options={originOptions}
              activeOrigin={activeOrigin}
              onSelect={onSelectOrigin}
              allowSwitch={allowSwitchOrigin}
            />
          )}

          {/* Selected count badge */}
          {selectedCount > 0 && (
            <div style={workspaceStyles.selectedBadge}>
              <span>
                {isVeryCompact
                  ? `${selectedCount}x`
                  : `${selectedCount} selecionada${selectedCount !== 1 ? 's' : ''}`}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClear?.();
                }}
                style={workspaceStyles.clearBtn}
                title="Limpar seleção"
              >
                <X size={12} />
              </button>
            </div>
          )}

          {/* Botao auxiliar de historico — alinhado a direita. O "Configurar
              workspace" ficou apenas no menu de 3 pontinhos para nao poluir
              a barra de acoes. */}
          {hasHistory && (
            <div style={workspaceStyles.panelToggleGroup}>
              <button
                style={workspaceStyles.historyBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleHistory?.();
                }}
                onMouseEnter={(e) => {
                  if (!isHistoryOpen) {
                    e.currentTarget.style.backgroundColor = workspaceColors.bgSecondary;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isHistoryOpen) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
                title={isHistoryOpen ? 'Fechar histórico' : 'Histórico gerado pela IA'}
              >
                <History size={12} />
                {!isVeryCompact && (
                  <span>{isHistoryOpen ? 'Fechar Histórico' : 'Histórico'}</span>
                )}
              </button>
            </div>
          )}
        </div>

        {/* ✅ Área de conteúdo com scroll vertical */}
        <div style={workspaceStyles.contentArea} className="workspace-content-scroll">
          {/* History Section */}
          {isHistoryOpen && hasHistory && (
            <>
              {/* ✅ Header sticky */}
              <div style={workspaceStyles.historySectionHeader}>
                <span style={workspaceStyles.historySectionTitle}>
                  <History size={12} />
                  Histórico ({historyItems.length})
                   <button
                  style={workspaceStyles.clearHistoryBtn}
                  onClick={onClearHistory}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = `${workspaceColors.danger}15`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  Limpar Histórico
                </button>
                </span>
               
              </div>
              {/* Cards do histórico */}
              <div style={workspaceStyles.historyCardsContainer}>
                <ScrollableCardRow workspaceColors={workspaceColors}>
                  {historyItems.map((item) => (
                    <ResultCard
                      key={`history-${item.id}`}
                      result={item}
                      onUseResponse={onUseResponse}
                      onRemove={() => onRemoveFromHistory?.(item.id)}
                      remainingRegenerations={0}
                      isRegenerating={false}
                      isHistoryItem={true}
                    />
                  ))}
                </ScrollableCardRow>
              </div>
            </>
          )}

          {/* Current Results Section */}
          {hasAnyResults && (
            <>
              {isHistoryOpen && hasHistory && (
                <div style={workspaceStyles.currentSectionHeader}>
                  Resultados Atuais
                </div>
              )}
              <ScrollableCardRow workspaceColors={workspaceColors}>
                {/* Translate Results */}
                {translateResults.map((result) => (
                  <ResultCard
                    key={result.id}
                    result={result}
                    onUseResponse={onUseResponse}
                    onRegenerate={() => onRegenerateTranslate?.(result)}
                    remainingRegenerations={getTranslateRemainingRegenerations?.(result.id) ?? MAX_REGENERATIONS}
                    isRegenerating={regeneratingId === result.id}
                    onRemove={() => onRemoveTranslateResult?.(result.id)}
                  />
                ))}

                {/* Generate Results */}
                {generateResults.map((result) => (
                  <ResultCard
                    key={result.id}
                    result={result}
                    onUseResponse={onUseResponse}
                    onRegenerate={() => onRegenerateGenerate?.(result)}
                    remainingRegenerations={getGenerateRemainingRegenerations?.(result.id) ?? MAX_REGENERATIONS}
                    isRegenerating={regeneratingId === result.id}
                    onRemove={() => onRemoveGenerateResult?.(result.id)}
                  />
                ))}

                {/* Custom Results */}
                {customResults.map((result) => (
                  <ResultCard
                    key={result.id}
                    result={result}
                    onUseResponse={onUseResponse}
                    onRegenerate={() => onRegenerateCustom?.(result)}
                    remainingRegenerations={getCustomRemainingRegenerations?.(result.id) ?? MAX_REGENERATIONS}
                    isRegenerating={regeneratingId === result.id}
                    onRemove={() => onRemoveCustomResult?.(result.id)}
                  />
                ))}
              </ScrollableCardRow>
            </>
          )}
        </div>
      </div>

      <style>{`
        .hide-scrollbar-workspace::-webkit-scrollbar {
          display: none;
        }
        
        /* Scrollbar estilizada para área de conteúdo */
        .workspace-content-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .workspace-content-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .workspace-content-scroll::-webkit-scrollbar-thumb {
          background-color: rgba(128, 128, 128, 0.3);
          border-radius: 3px;
        }
        .workspace-content-scroll::-webkit-scrollbar-thumb:hover {
          background-color: rgba(128, 128, 128, 0.5);
        }
      `}</style>
    </>
  );
};

export default AIWorkspace;